import { useState, useCallback } from 'react';
import { ChevronDown, FolderOpen, Shield } from 'lucide-react';

/**
 * CadernoAccordion — Acordeão colapsável para Cadernos de Autos.
 * Implementa o padrão de Divulgação Progressiva (Progressive Disclosure)
 * conforme benchmark UX dos tribunais (e-SAJ Pasta Digital / e-Proc Tree-View).
 *
 * Props:
 *   cadernoId    — ID do caderno (string)
 *   cadernoTitle — Título exibido no cabeçalho (string)
 *   docs         — Array de documentos pertencentes a este caderno
 *   defaultOpen  — Se o acordeão deve iniciar expandido (boolean, default: false)
 *   isAvulso     — Se é o grupo "Documentos Avulsos" sem caderno vinculado (boolean)
 *   renderDocRow — Callback (row, index) => JSX para renderizar cada documento
 *   processNumber — NPU do processo vinculado (string, opcional)
 *   juizo        — Juízo / Vara (string, opcional)
 *   comarca      — Comarca / Foro (string, opcional)
 *   className    — Classes CSS adicionais no container (string)
 */
const CadernoAccordion = ({
  cadernoId = '',
  cadernoTitle = '',
  docs = [],
  defaultOpen = false,
  isAvulso = false,
  renderDocRow,
  processNumber = '',
  juizo = '',
  comarca = '',
  className = '',
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const safeDocs = Array.isArray(docs) ? docs : [];
  const docCount = safeDocs.length;
  const displayLabel = isAvulso ? 'Documentos Avulsos' : (cadernoTitle || 'Processo');

  const toggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  if (docCount === 0) return null;

  // Documentos avulsos: renderizar flat (sem acordeão colapsável)
  if (isAvulso) {
    return (
      <div className={`space-y-2 ${className}`}>
        <div className="flex items-center gap-2 px-1">
          <FolderOpen className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <div className="text-[11px] font-bold text-slate-500">{displayLabel}</div>
          <div className="flex-1 border-b border-slate-200" />
          <div className="text-[10px] font-bold text-slate-400">{docCount}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
          {safeDocs.map((row, idx) => renderDocRow ? renderDocRow(row, idx) : null)}
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border transition-colors duration-200 ${open ? 'border-[#C5A059]/40 bg-[#C5A059]/[0.02] shadow-sm' : 'border-gray-200 bg-white'} ${className}`}>
      {/* Cabeçalho do Acordeão — clicável */}
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center gap-2.5 px-3 py-2 text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059]/50 rounded-xl transition-colors"
        aria-expanded={open}
        aria-controls={`caderno-panel-${cadernoId}`}
      >
        {/* Ícone do caderno */}
        <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 transition-colors ${open ? 'bg-[#C5A059]/10 text-[#C5A059]' : 'bg-slate-100 text-slate-500 group-hover:bg-[#C5A059]/10 group-hover:text-[#C5A059]'}`}>
          <Shield className="w-3.5 h-3.5" />
        </div>

        {/* Título + Contagem */}
        <div className="flex-1 min-w-0">
          <div className={`text-[12px] font-bold truncate transition-colors ${open ? 'text-[#20243B]' : 'text-slate-700 group-hover:text-[#20243B]'}`}>
            {displayLabel}
          </div>
          <div className="text-[10px] text-slate-400 truncate">
            {[processNumber, juizo, comarca].filter(Boolean).length > 0
              ? [processNumber, juizo, comarca].filter(Boolean).join(' · ')
              : `${docCount} documento${docCount !== 1 ? 's' : ''}`
            }
          </div>
        </div>

        {/* Badge de contagem + Chevron */}
        <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full transition-colors ${open ? 'bg-[#C5A059] text-white' : 'bg-slate-100 text-slate-500'}`}>
          {docCount}
        </span>
        <ChevronDown
          className={`w-4 h-4 shrink-0 transition-transform duration-200 ${open ? 'rotate-180 text-[#C5A059]' : 'text-slate-400 group-hover:text-[#C5A059]'}`}
        />
      </button>

      {/* Painel de documentos — colapsável */}
      <div
        id={`caderno-panel-${cadernoId}`}
        role="region"
        aria-labelledby={`caderno-header-${cadernoId}`}
        className={`overflow-hidden transition-all duration-200 ease-in-out ${open ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className={`${open ? 'border-t border-slate-100 bg-white' : ''}`}>
          {safeDocs.map((row, idx) => renderDocRow ? renderDocRow(row, idx) : null)}
        </div>
      </div>
    </div>
  );
};

export { CadernoAccordion };
export default CadernoAccordion;
